import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is logged in on mount
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');

        if (token && savedUser && savedUser !== 'undefined') {
            try {
                const parsedUser = JSON.parse(savedUser);
                if (parsedUser && typeof parsedUser === 'object') {
                    setUser(parsedUser);
                } else {
                    // Invalid user object
                    localStorage.removeItem('user');
                    localStorage.removeItem('token');
                }
            } catch (error) {
                console.error('Error parsing saved user:', error);
                localStorage.removeItem('user');
                localStorage.removeItem('token');
            }
        } else if (savedUser === 'undefined') {
            // Clean up invalid state
            localStorage.removeItem('user');
            if (!token) localStorage.removeItem('token');
        }

        setLoading(false);
    }, []);

    const login = async (email, password) => {
        try {
            const response = await api.post('/auth/login', { email, password });
            const { user, token } = response.data.data;

            localStorage.setItem('token', token);
            if (user) {
                try {
                    localStorage.setItem('user', JSON.stringify(user));
                    setUser(user);
                } catch (e) {
                    console.error('Error saving user to localStorage:', e);
                    localStorage.removeItem('user');
                }
            } else {
                localStorage.removeItem('user');
            }

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Błąd podczas logowania'
            };
        }
    };

    const register = async (userData) => {
        try {
            const response = await api.post('/auth/register', userData);

            // Check if email verification is required
            if (response.data.requiresVerification) {
                return {
                    success: true,
                    requiresVerification: true,
                    message: response.data.message,
                    email: response.data.email
                };
            }

            // Check if added to waiting list
            if (response.data.isWaitingList) {
                return {
                    success: true,
                    isWaitingList: true,
                    message: response.data.message
                };
            }

            // Old flow (if backend returns token - for backward compatibility)
            const { user, token } = response.data.data || response.data;

            localStorage.setItem('token', token);
            if (user) {
                try {
                    localStorage.setItem('user', JSON.stringify(user));
                    setUser(user);
                } catch (e) {
                    console.error('Error saving user to localStorage (register):', e);
                    localStorage.removeItem('user');
                }
            } else {
                localStorage.removeItem('user');
            }

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Błąd podczas rejestracji'
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const response = await api.get('/auth/me');
            const userData = response.data.data.user;
            if (userData) {
                try {
                    localStorage.setItem('user', JSON.stringify(userData));
                    setUser(userData);
                } catch (e) {
                    console.error('Error saving user to localStorage (refresh):', e);
                    localStorage.removeItem('user');
                }
                return userData;
            } else {
                return null;
            }
        } catch (error) {
            console.error('Error refreshing user:', error);
            return null;
        }
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
